import React, { useState } from "react";
import { View, TextInput, TouchableOpacity, Text, StyleSheet, Keyboard, Platform, PixelRatio } from "react-native";
import { COLORS, STRINGS, errorMessages } from "../../constants";
import EventStore from "../../storage/eventStore";
import { faDeleteLeft } from "@fortawesome/free-solid-svg-icons";
import { verifySearchText } from "../../utils/helper";

/**
 * SearchbarComponent renders a custom search bar using TextInput with a Clear button inside, and a Search button outside.
 */
const SearchbarComponent = ({ FontAwesomeIcon, size }) => {
  const { clearText, searchText, setSearchText, overrideVerify, resetVerify } = EventStore();
  const [localText, setLocalText] = useState(searchText);
  const [textCheck, setTextCheck] = useState({ pass: true, errors: [] });
  const selectedCategories = EventStore((state) => state.selectedCategories || []);

  const onChangeText = (text) => {
    setLocalText(text);
  };

  const onPressSearch = () => {
    const validation = verifySearchText(localText);
    const hasSelectedCategories = selectedCategories.length > 0;

    if (!validation.pass && !hasSelectedCategories) {
      setTextCheck(validation);
      return;
    }

    setSearchText(localText);
    setTextCheck({ pass: true, errors: [] });
    if (Platform.OS === "web") {
      const searchInputElement = document.getElementById("searchInput");
      searchInputElement.focus();
    } else {
      Keyboard.dismiss();
    }
  };

  const onKeypress = (event) => {
    if (event.nativeEvent.key === "Enter") {
      onPressSearch();
      if (Platform.OS === "web") {
        event.preventDefault();
        event.target.focus();
      }
    }
  };

  const onClear = () => {
    setLocalText("");
    clearText();
    setTextCheck({ pass: true, errors: [] });
  };

  if (overrideVerify) {
    setTextCheck({ pass: true, errors: [] });
    resetVerify(false);
  }

  const displayErrorMessage = () => {
    if (!textCheck.pass) {
      return textCheck.errors.map((error, index) => (
        <View key={index} style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ));
    }
  };

  // Function to determine if multiline should be enabled based on platform and font scale
  const makeMultiline = () => {
    const ratio = PixelRatio.getFontScale();
    if (Platform.OS === "ios" && ratio > 1.4) {
      return true;
    }
    return false;
  };

  return (
    <View>
      <View style={styles.wrapper}>
        <View style={styles.inputContainer}>
          <TextInput
            placeholder={STRINGS.searchEvents}
            onChangeText={onChangeText}
            value={localText}
            style={styles.input}
            placeholderTextColor={COLORS.border}
            returnKeyType="search"
            onSubmitEditing={onPressSearch}
            onKeyPress={onKeypress}
            allowFontScaling={true}
            multiline={makeMultiline()}
            id="searchInput"
          />
          {localText.length > 0 && (
            <TouchableOpacity onPress={onClear} style={styles.clearButton} accessibilityLabel={STRINGS.clearSearch}>
              <FontAwesomeIcon icon={faDeleteLeft} size={size} color={COLORS.border} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity onPress={onPressSearch} style={styles.searchButton}>
          <Text style={styles.searchButtonText}>{STRINGS.search}</Text>
        </TouchableOpacity>
      </View>
      {!textCheck.pass && displayErrorMessage()}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "center",
    margin: 16,
  },
  inputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.backgroundColorDark,
    borderColor: COLORS.border,
    borderWidth: 0.5,
    borderRadius: 50,
    paddingHorizontal: 12,
    minHeight: Platform.OS == "web" ? 50 : 42,
  },
  input: {
    flex: 1,
    fontSize: 15,
    fontWeight: "500",
    color: COLORS.textDarker,
    paddingVertical: 6,
    paddingHorizontal: 6,
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    backgroundColor: "transparent",
  },
  errorContainer: {
    flexDirection: "row",
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: COLORS.warningLight,
    borderWidth: 1,
    borderColor: COLORS.warning,
  },
  errorText: {
    color: COLORS.warning,
    fontSize: 14,
    lineHeight: 20,
  },
  clearButton: {
    padding: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  searchButton: {
    backgroundColor: COLORS.blueBackground,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 25,
    marginLeft: Platform.OS == "web" ? 8 : 6,
    justifyContent: "center",
    alignItems: "center",
    minHeight: Platform.OS == "web" ? 50 : 42,
    borderWidth: 2,
    borderColor: COLORS.secondary,
  },
  searchButtonText: {
    color: COLORS.secondary,
    fontWeight: "600",
    fontSize: 14,
  },
});

export default SearchbarComponent;
